import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getYoutubeId } from '../../../../utils/index';
import { ModuleWrapper } from '../moduleWrapper';
import {
  Subtitle,
  SectionContainer,
  ButtonsContainer,
  InputVideo,
  ErrorMessage,
  AddButton,
  VideoList,
  Video,
  VideoDetail,
  TrashIcon
} from './styles.js';
import {
  getVideos,
  addMultimedia,
  MultimediaType,
  MultimediaCollection,
  deleteMultimedia
} from '../../../../ducks/multimedia';
import useSectionsCollapse from '../../../../hooks/useSectionsCollapse.js';
import ToggleButton from '../../../commons/ToggleButton';

export const Videos = ({
  register,
  errors,
  trigger,
  setValue,
  getValues,
  setValidateVideo,
  validateVideo,
  clearErrors,
  device,
  watch
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { videos } = useSelector(getVideos);
  const limitedVideosDevice = { mobile: 4, tablet: 4, desktop: 4 };
  const showLimitedVideos = videos.length > limitedVideosDevice[device];
  const { registerRef, getMaxHeight, isCollapsed, setCollapsed } = useSectionsCollapse({
    visibleItems: limitedVideosDevice[device],
    itemSelector: '.video-item',
    dependencies: [videos.length]
  });
  const watchVideo = watch('videoUrl');

  const deleteVideo = id => {
    dispatch(deleteMultimedia(id, MultimediaCollection.VIDEO));
  };

  useEffect(() => {
    const addVideo = () => {
      const videoUrl = getValues('videoUrl');
      if (!videoUrl) return;

      const objVideo = {
        key_video: getYoutubeId(videoUrl),
        multimediaTypeEnum: MultimediaType.VIDEO
      };
      setValue('videoUrl', '');
      dispatch(addMultimedia(objVideo, MultimediaCollection.VIDEO));
    };

    const processValidation = async () => {
      const result = await trigger('videoUrl');
      if (result) {
        addVideo();
      }
    };

    if (validateVideo) {
      processValidation();
    }
    return setValidateVideo(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validateVideo]);

  return (
    <ModuleWrapper padding={showLimitedVideos ? '0px 0px 8px' : '0'}>
      <Subtitle data-publi='multimedia-video-subtitle'>{t('professionalPubli.multimedia.video.subtitle')}</Subtitle>
      <SectionContainer data-publi='multimedia-video-sectionContainer'>
        <ButtonsContainer device={device} data-publi='multimedia-video-buttonContainer'>
          <div className='input-container'>
            <InputVideo
              data-publi='multimedia-videos-input'
              className={errors.videoUrl && 'onError'}
              type='text'
              disabled={videos.length >= 10}
              name='videoUrl'
              ref={register}
              onKeyDown={evt => {
                if (evt.key === 'Enter') evt.preventDefault();
              }}
              placeholder={t('professionalPubli.multimedia.video.placeholder')}
              onFocus={() => clearErrors('videoUrl')}
            />
            {errors.videoUrl && errors.videoUrl.type === 'matches' && (
              <ErrorMessage data-publi='multimedia-video-error-invalidUrl'>
                {t('professionalPubli.multimedia.video.error.invalid')}
              </ErrorMessage>
            )}
            {errors.videoUrl && errors.videoUrl.type === 'existInArray' && (
              <ErrorMessage data-publi='multimedia-video-error-repeatUrl'>
                {t('professionalPubli.multimedia.video.error.duplicated')}
              </ErrorMessage>
            )}
          </div>
          <AddButton
            data-publi='multimedia-videos-button-add'
            device={device}
            type='button'
            disabled={videos.length >= 10 || watchVideo === '' || errors.videoUrl}
            onClick={() => {
              if (videos.length < 10) {
                setValidateVideo(true);
              }
            }}
          >
            {t('professionalPubli.multimedia.video.add')}
          </AddButton>
        </ButtonsContainer>
        {videos.length > 0 && (
          <VideoList
            device={device}
            data-publi='multimedia-videos-itemlist'
            ref={registerRef}
            maxHeight={getMaxHeight()}
            hasToggleButton={showLimitedVideos}
          >
            {videos.map(video => (
              <Video
                data-publi={`multimedia-videos-item-${video.videoId}`}
                key={video.id}
                className='video-item'
                device={device}
              >
                <iframe
                  data-publi={`multimedia-videos-item-${video.videoId}-iframe`}
                  title={video.videoId}
                  width='100%'
                  height='100%'
                  src={`https://www.youtube-nocookie.com/embed/${video.key_video}?modestbranding=1&rel=0&showinfo=0`}
                  frameBorder='0'
                  allowFullScreen
                />
                <VideoDetail data-publi={`multimedia-videos-item-${video.key_video}-videoDetail`} device={device}>
                  <span>{`https://www.youtube.com/watch?v=${video.key_video}`}</span>
                  <TrashIcon
                    data-publi={`multimedia-videos-item-${video.key_video}-button-delete`}
                    onClick={() => deleteVideo(video.id)}
                  />
                </VideoDetail>
              </Video>
            ))}
          </VideoList>
        )}
        {showLimitedVideos && (
          <ToggleButton
            dataPubli='multimedia-video-showMore'
            device={device}
            title={t(`professionalPubli.multimedia.video.${isCollapsed() ? 'showMore' : 'showLess'}`)}
            active={!isCollapsed()}
            setActive={() => setCollapsed()}
          />
        )}
      </SectionContainer>
    </ModuleWrapper>
  );
};
